import { operations } from '@/generated/supplier-experience-api';

type FindAllSupplierMarketsResponse =
  operations['findAllSupplierMarkets']['responses'][200]['content']['application/json']['data'];
type SupplierMarketEntry = FindAllSupplierMarketsResponse['supplierMarketGroups'][number]['supplierMarkets'][number];

export type InstantPayEnrollment = NonNullable<SupplierMarketEntry['supplier']['instantPay']>;

const INSTANT_PAY_CURRENCIES = ['USD'] as const;

interface InstantPayTakerMarket {
  takerDivisionUuid: string;
  instantPay?: InstantPayEnrollment | null;
}

export function getInstantPayEnrollment(supplier: {
  instantPay?: InstantPayEnrollment | null;
}): InstantPayEnrollment | null {
  return supplier.instantPay ?? null;
}

export function isInstantPayDivisionEnabled(instantPay: InstantPayEnrollment | null | undefined): boolean {
  return instantPay?.isEnabled === true;
}

export function isInstantPayUsdMarket(currency: string): boolean {
  return (INSTANT_PAY_CURRENCIES as readonly string[]).includes(currency);
}

export function isInstantPayEnabledUsdMarket(
  instantPay: InstantPayEnrollment | null | undefined,
  currency: string
): boolean {
  return isInstantPayDivisionEnabled(instantPay) && isInstantPayUsdMarket(currency);
}

export function hasCustodialFundingConfirmed(instantPay: InstantPayEnrollment | null | undefined): boolean {
  return instantPay?.custodialFundingConfirmedAt != null;
}

export function hasOnboardingCompleted(instantPay: InstantPayEnrollment | null | undefined): boolean {
  return instantPay?.onboardingCompletedAt != null;
}

export function uniqueInstantPayEnrollments(takerMarkets: InstantPayTakerMarket[]): Array<InstantPayEnrollment | null> {
  const byDivision = new Map<string, InstantPayEnrollment | null>();

  for (const market of takerMarkets) {
    if (!byDivision.has(market.takerDivisionUuid)) {
      byDivision.set(market.takerDivisionUuid, market.instantPay ?? null);
    }
  }

  return [...byDivision.values()];
}

export function isAllDivisionsOnboardingComplete(takerMarkets: InstantPayTakerMarket[]): boolean {
  const enrollments = uniqueInstantPayEnrollments(takerMarkets);

  if (enrollments.length === 0) {
    return false;
  }

  return enrollments.every((enrollment) => hasOnboardingCompleted(enrollment));
}

export function isBuyerRemittanceComplete(buyer: { takerMarkets: InstantPayTakerMarket[] }): boolean {
  const enabledMarkets = buyer.takerMarkets.filter((market) => isInstantPayDivisionEnabled(market.instantPay));

  if (enabledMarkets.length === 0) {
    return false;
  }

  return uniqueInstantPayEnrollments(enabledMarkets).every((enrollment) => hasCustodialFundingConfirmed(enrollment));
}

// Reason: a division only produces substitutions once it has both finished onboarding and confirmed
// custodial funding, so both timestamps must sit on the same division. isEnabled is ignored so a
// division disabled after going live keeps access to its past records.
export function hasActiveInstantPayDivision(takerMarkets: InstantPayTakerMarket[]): boolean {
  return uniqueInstantPayEnrollments(takerMarkets).some(
    (enrollment) => hasOnboardingCompleted(enrollment) && hasCustodialFundingConfirmed(enrollment)
  );
}
